import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../../core/components/Button.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import ActionBlock from '../components/ActionBlock.tsx';
import AddActionDropdown from '../components/AddActionDropdown.tsx';
import type { ActionType } from '../types.ts';

const TrashIcon = withIcon(faTrash);

const MappingHeader = () => {
    const { t } = useTranslation('integrations');
    const { selectedMapping, selectedMappingIndex, deleteMapping } = useOutputMapperEditor();

    if (!selectedMapping || selectedMappingIndex === null) return null;

    return (
        <div className="flex flex-col gap-3 p-4 border-b border-line-weak bg-surface-medium">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-content-weak">{t('outputMapper.visualEditor.mappingTarget')}:</span>
                    <span className="font-medium text-content-strong">{selectedMapping.target}</span>
                </div>
                <Button
                    variant="ghost"
                    type="button"
                    className="text-status-danger-medium hover:text-status-danger-strong"
                    onClick={() => deleteMapping(selectedMappingIndex)}
                >
                    <TrashIcon className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

const ChainBuilderPanel = () => {
    const { t } = useTranslation('integrations');
    const { selectedMapping, selectedMappingIndex, addAction, availableActionTypes } = useOutputMapperEditor();

    if (!selectedMapping || selectedMappingIndex === null) {
        return (
            <div className="h-full flex items-center justify-center text-content-weak">
                <div className="text-center">
                    <div className="text-lg mb-2">{t('outputMapper.visualEditor.noMappingSelected')}</div>
                    <div className="text-sm">{t('outputMapper.visualEditor.selectOrCreateMapping')}</div>
                </div>
            </div>
        );
    }

    const handleAddAction = (actionType: ActionType, insertIndex?: number) => {
        addAction(selectedMappingIndex, actionType, insertIndex);
    };

    return (
        <div className="h-full flex flex-col">
            <MappingHeader />
            <div className="flex-1 overflow-auto p-4">
                <div className="flex flex-col gap-2">
                    {selectedMapping.actions.map((action, index) => (
                        <React.Fragment key={`${selectedMappingIndex}-${index}`}>
                            {index > 0 && (
                                <AddActionDropdown
                                    actionTypes={availableActionTypes}
                                    onAddAction={(at) => handleAddAction(at, index)}
                                    variant="inline"
                                />
                            )}
                            <ActionBlock
                                action={action}
                                index={index}
                                mappingIndex={selectedMappingIndex}
                                actionId={`${selectedMappingIndex}-${index}`}
                            />
                        </React.Fragment>
                    ))}
                    <div className={classnames(selectedMapping.actions.length > 0 && 'mt-4')}>
                        <AddActionDropdown
                            actionTypes={availableActionTypes}
                            onAddAction={(at) => handleAddAction(at)}
                            variant="default"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChainBuilderPanel;
